import { useCallback, useMemo, useState } from 'react';
import type { QueryResultSet } from '../../../../types/queryExecution';
import type { ResultsExportFormat } from '../../../../types/resultsExport';
import { toast } from '../../../../components/ui/toaster';
import { writeClipboardText } from '../../../../lib/clipboard';
import { tableDataToCsv, tableDataToHtml, tableDataToMarkdown, writeRichToClipboard } from '../../common/utils/clipboardFormatters';
import { exportResults } from '../../common/services/resultsExportApi';
import { trackStorageEvent } from '../services/storageAnalytics';
import type { QueueMessageRow, QueueMessageSet } from './queueMessagesStore';
import { queueMessageColumns, queueMessageExportRows } from './queueMessageColumns';

export function useQueueMessageCopyExport(rows: QueueMessageRow[], set: QueueMessageSet, decodeBase64: boolean, now: number) {
  const columns = useMemo(() => queueMessageColumns(set).map(({ name }) => ({ id: name, name })), [set]);
  const records = useMemo(() => queueMessageExportRows(rows, set, decodeBase64, now), [rows, set, decodeBase64, now]);
  const resultSet = useMemo<QueryResultSet>(() => ({ columns, rows: records, rowCount: records.length }), [columns, records]);
  const formatterColumns = useMemo(() => columns.map(({ name }) => ({ name })), [columns]);
  const [copied, setCopied] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Same events as the blob browser's copy / export menu, tagged with the queue mode.
  const handleCopy = useCallback(async (format: 'csv' | 'markdown' | 'insert' | 'html') => {
    if (format === 'insert') return;
    try {
      if (format === 'csv') await writeClipboardText(tableDataToCsv(records, formatterColumns, '*'));
      else if (format === 'markdown') await writeClipboardText(tableDataToMarkdown(records, formatterColumns, '*'));
      else await writeRichToClipboard(tableDataToCsv(records, formatterColumns, '*'), tableDataToHtml(records, formatterColumns, '*'));
    } catch (error) {
      toast.error({ title: 'Copy failed', description: error instanceof Error ? error.message : 'The clipboard is unavailable.' });
      return;
    }
    trackStorageEvent('toolbar:storage_copy', { format, mode: 'messages' });
    setCopied(true); window.setTimeout(() => setCopied(false), 1500);
  }, [records, formatterColumns]);
  const handleExport = useCallback(async ({ format }: { format: ResultsExportFormat; scope: 'active' | 'all' }) => {
    if (format === 'sql') return { success: false as const };
    setIsExporting(true);
    try {
      // The shared wrapper records `feature:export_results` for a written file.
      const result = await exportResults({ format, resultSets: [resultSet], suggestedFileName: 'queue-messages' });
      trackStorageEvent('toolbar:storage_export', { format, mode: 'messages', outcome: result.success ? 'ok' : result.cancelled ? 'cancelled' : 'error' });
      if (!result.success && !result.cancelled) toast.error({ title: 'Export failed', description: result.error });
      return result;
    } finally { setIsExporting(false); }
  }, [resultSet]);
  return { resultSet, copied, isExporting, handleCopy, handleExport };
}
